import { ClaimStatus, RECOUPMENT_PROBABILITY_LABELS, RECOUPMENT_REASON_LABELS } from '../../constants';
import { MAX_CLAIM_ANCESTOR_LIMIT, SORT_ASCENDING, SORT_DESCENDING } from './constants';

import type { RecoupmentProbabilityType, RecoupmentReasonType } from '../../constants';
import type { IClaim, ISelectOption, SelectValueType } from '../../../../../../utils/types';

/**
 * Claim overview status dropdown helpers: build sorted options and compute which
 * values are disabled based on the current primary or secondary claim status.
 */

/** All claim statuses as select options, A–Z by label (case-insensitive). */
const getSortedStatusOptions = (): ISelectOption[] =>
  Object.values(ClaimStatus)
    .map((status) => ({
      value: status,
      label: status,
    }))
    .sort((a, b) => {
      const aLabel = String(a.label).toLowerCase();
      const bLabel = String(b.label).toLowerCase();

      if (aLabel < bLabel) return SORT_ASCENDING;
      if (aLabel > bLabel) return SORT_DESCENDING;
      return 0;
    });

/**
 * Whether a status option should be disabled in the dropdown for a given
 * current claim status (primary or secondary — same rules, different inputs).
 *
 * - Rejected & Resubmitted: always disabled (not selectable from the list).
 * - Approved - Recouped & Resubmitted: always disabled (not selectable from the list).
 * - While status is Unbillable: only Submitted may be selected; everything else is disabled.
 * - Unbillable as a target value: only allowed when current status is Generated or Submitted;
 *   otherwise picking Unbillable is disabled.
 */
const isStatusOptionDisabled = (
  optionValue: ISelectOption['value'],
  claimStatus?: string | null,
): boolean =>
  optionValue === ClaimStatus.REJECTED_AND_RESUBMITTED ||
  optionValue === ClaimStatus.APPROVED_RECOUPED_RESUBMITTED ||
  (claimStatus === ClaimStatus.UNBILLABLE && optionValue !== ClaimStatus.SUBMITTED) ||
  (claimStatus !== ClaimStatus.GENERATED &&
    claimStatus !== ClaimStatus.SUBMITTED &&
    optionValue === ClaimStatus.UNBILLABLE) ||
  (claimStatus !== ClaimStatus.APPROVED && optionValue === ClaimStatus.APPROVED_RECOUPED);

/**
 * Builds two parallel option lists: primary claim status and secondary claim status.
 * Disabled flags use `claimStatus` for the first list and `secondaryClaimStatus` for the second,
 * so primary and secondary dropdowns can differ when their current statuses differ.
 */
export const getClaimStatusOptions = (
  claimStatus?: string | null,
  secondaryClaimStatus?: string | null,
): [ISelectOption[], ISelectOption[]] => {
  const sortedStatusOptions = getSortedStatusOptions();
  const statusOptions = sortedStatusOptions.map((option) => ({
    ...option,
    disabled: isStatusOptionDisabled(option.value, claimStatus),
  }));
  const secClaimStatusOptions = sortedStatusOptions.map((option) => ({
    ...option,
    disabled: isStatusOptionDisabled(option.value, secondaryClaimStatus),
  }));

  return [statusOptions, secClaimStatusOptions];
};

export const isRecoupmentProbability = (
  value: SelectValueType,
): value is RecoupmentProbabilityType => value in RECOUPMENT_PROBABILITY_LABELS;

export const isRecoupmentReason = (value: SelectValueType): value is RecoupmentReasonType =>
  value in RECOUPMENT_REASON_LABELS;

/**
 * Primary claims can be corrected when they either explicitly need resubmission,
 * or when an approved claim was recouped and still has room in the correction chain.
 * Recouped claims cannot already have a corrected descendant, and the ancestor
 * limit prevents creating correction chains beyond the clearinghouse limit.
 */
export const shouldShowPrimaryCorrectClaimButton = (
  claim?: Pick<IClaim, 'ancestors' | 'descendants' | 'status'> | null,
): boolean =>
  claim?.status === ClaimStatus.REJECTED_NEEDS_RESUBMISSION ||
  (claim?.status === ClaimStatus.APPROVED_RECOUPED &&
    claim.descendants.length === 0 &&
    claim.ancestors.length < MAX_CLAIM_ANCESTOR_LIMIT);

export const shouldShowSecondaryCorrectClaimButton = (
  claim?: Pick<IClaim, 'ancestors' | 'descendants' | 'secondary_status'> | null,
): boolean =>
  Boolean(
    claim?.secondary_status &&
    (claim.secondary_status === ClaimStatus.REJECTED_NEEDS_RESUBMISSION ||
      claim.secondary_status === ClaimStatus.APPROVED_RECOUPED) &&
    claim.descendants.length === 0 &&
    claim.ancestors.length < MAX_CLAIM_ANCESTOR_LIMIT,
  );
